import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { UserAuth } from '../../auth/Auth';
import { isURL } from '../../../environment/Helper';
import { PrivateAxios, url } from '../../../environment/AxiosInstance';
import { ErrorMessage, SuccessMessage } from '../../../environment/ToastMessage';


const CloseTaskModal = ({ show, handleClose, task, onUpdated }) => {
  const { User, MatchPermission } = UserAuth();

  // ================== ALL HOOKS MUST BE AT THE TOP ==================
  // Dark theme
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  // Controlled textarea for close remarks
  const [closeReason, setCloseReason] = useState("");

  const [loading, setLoading] = useState(false);

  // Detect dark theme
  useEffect(() => {
    const checkTheme = () => setIsDarkTheme(document.body.classList.contains("dark-theme"));
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Update closeReason when task changes
  useEffect(() => {
    if (task) setCloseReason(task.close_remarks || "");
  }, [task]);

  // ================== PERMISSION CHECK AFTER ALL HOOKS ==================
  // Only allow users with Edit Project permission to close tasks
  if (!MatchPermission(['Edit Project', 'Project All', 'Doer Edit Project'])) {
    return null;
  }

  if (!task) return null;

  // Helper to get user by ID
  const getUserById = (id) => {
    if (!User) return null;
    return User.find(u => u.id === id) || null;
  };

  // Submit close task
  const handleSubmit = async () => {
    if (!task) return;
    setLoading(true);
    try {
      await PrivateAxios.put(`/projecttasks/${task.id}/close`, {
        close_remarks: closeReason,
      });
      if (onUpdated) onUpdated(); // callback to parent to refresh data
      handleClose();
      SuccessMessage("Task closed successfully");
    } catch (error) {
      console.error("Failed to close task:", error);
      ErrorMessage("Failed to close task");
    } finally {
      setLoading(false);
    }
  };

  // Reusable profile component
  const ProfileCell = ({ userId }) => {
    const user = getUserById(userId);
    if (!user) return <span>-</span>;

    return (
      <div className="profile-wrap">
        <div className="exp-avtar bg-exp-yellow">
          {user.user_photo ? (
            <img
              className="prof-img"
              src={isURL(user.user_photo) ? user.user_photo : url + user.user_photo}
              alt={user.name}
            />
          ) : (
            <span>
              {user.name
                ? user.name.split(' ').length > 1
                  ? user.name.split(' ')[0][0] + user.name.split(' ')[1][0]
                  : user.name.slice(0, 2)
                : ''}
            </span>
          )}
        </div>
        <div className="ps-2 profile-name-wrap text-truncate">
          <h5 className="profile-name text-nowrap text-truncate">{user.name}</h5>
        </div>
      </div>
    );
  };

  return (
    <Modal
      id="CloseTaskModal"
      show={show}
      onHide={handleClose}
      backdrop="static"
      centered
      size="md"
    >
      <Modal.Header closeButton>
        <Modal.Title className="gth-modal-title">Close This Task</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pb-1">
        <div className="row">
          <div className="col-lg-12">
            <div className="form-group">
              <p className="fs-5 fw-bold">{task.task_name}</p>
            </div>
          </div>

         <div className="col-lg-6">
  <div className="form-group">
    <label className="form-label">Doer</label>
    <div className="d-flex flex-wrap">
      {(Array.isArray(task.assigned_to)
        ? task.assigned_to
        : typeof task.assigned_to === "string"
        ? JSON.parse(task.assigned_to)
        : [task.assigned_to]
      ).map((userId) => (
        <div key={userId} className="me-2 mb-2">
          <ProfileCell userId={userId} />
        </div>
      ))}
    </div>
  </div>
</div>

          <div className="col-lg-6">
            <div className="form-group">
              <label className="form-label">Assign By</label>
              <ProfileCell userId={task.assigned_by} />
            </div>
          </div>

          <div className="col-lg-12">
            <div className="form-group">
              <label className="form-label">Enter Reason</label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Enter Reason"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
              ></textarea>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="gth-blue-light-bg">
        <button type="button" className="btn" onClick={handleClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-exp-green"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Updating..." : "Update"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CloseTaskModal;
